import { Button, Modal } from 'lego-on-react';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { __RouterContext } from 'react-router';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/IntentsPage';
import block from 'propmods';

import { CurrentAliceAppContext } from '../../context/aliceAppContext';
import { SettingsPageContext, useSettingsPageContext } from '../../context/settingsPageContext';
import { Context as SnapshotContext } from '../../context/snapshotContext';
import { preventEsc } from '../../utils/events';
import { Handles as ConfirmHandles, UnmanagedConfirm } from '../Confirm/UnmanagedConfirm';
import { Header } from '../Form/Form';
import { GrammarPageEntitiesSettings } from '../GrammarPageEntitiesSettings/GrammarPageEntitiesSettings';

import './GrammarPageEntitiesSection.scss';

const b = block('GrammarPageEntitiesSection');
const t = i18n(keyset);

interface EntitiesSettingsPopupProps {
    onClose: (e: Event | undefined) => void;
}

const EntitiesSettingsPopup: React.FC<EntitiesSettingsPopupProps> = ({ onClose }) => {
    const skillContext = useContext(CurrentAliceAppContext)!;
    const routerContext = useContext(__RouterContext);
    const snapshotContext = useContext(SnapshotContext);
    const settingsPageContext = useSettingsPageContext();

    const confirmRef = useRef<ConfirmHandles>(null);

    const [isChanged, setIsChanged] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onModalClose = useCallback(
        preventEsc((e) => {
            isChanged
                ? confirmRef.current?.open({
                      title: t('warning-title'),
                      description: t('warning-message'),
                      onConfirm: () => onClose(e),
                  })
                : onClose(e);
        }),
        [isChanged],
    );

    return (
        <Modal autoclosable visible onOutsideClick={onModalClose} directions={['bottom-left']}>
            <div {...b('popup')}>
                <SnapshotContext.Provider value={snapshotContext}>
                    <SettingsPageContext.Provider value={settingsPageContext}>
                        <CurrentAliceAppContext.Provider value={skillContext}>
                            <__RouterContext.Provider value={routerContext}>
                                <GrammarPageEntitiesSettings
                                    onClose={onModalClose}
                                    afterSave={() => setIsChanged(false)}
                                    setIsChanged={setIsChanged}
                                />
                            </__RouterContext.Provider>
                        </CurrentAliceAppContext.Provider>
                    </SettingsPageContext.Provider>
                </SnapshotContext.Provider>
            </div>
            <UnmanagedConfirm ref={confirmRef} />
        </Modal>
    );
};

export const GrammarPageEntitiesSection: React.FC = () => {
    const [popupIsVisible, setPopupIsVisible] = useState(false);
    const { isEditable } = useSettingsPageContext();

    const onClick = useCallback(() => setPopupIsVisible(true), []);
    const onClose = useCallback(() => setPopupIsVisible(false), []);

    return (
        <div {...b()}>
            <Header title={t('entities-title')} subtitle={<>{t('entities-subtitle')}</>} />
            <Button theme="normal" size="s" onClick={onClick}>
                {isEditable ? t('button-edit') : t('button-view')}
            </Button>
            {popupIsVisible && <EntitiesSettingsPopup onClose={onClose} />}
        </div>
    );
};
